import axios from "axios";
import {
  CREATE_COMMENT_REPLY_URL,
  CREATE_COMMENT_URL,
  DELETE_COMMENT_REPLY_URL,
  DELETE_COMMENT_URL,
  GET_COMMENT_REPLIES_URL,
  GET_COMMENTS_URL,
  VOTE_COMMENT_REPLY_URL,
  VOTE_COMMENT_URL,
} from "../constants";

/**
 * Creates a comment on a specific post
 * @function createComment
 * @param {string} message The message to be posted as a comment
 * @param {string} postId The id of the post to comment on
 * @param {string} authToken A user's auth token
 * @returns {object} The created comment
 */
export const createComment = async (content, postId, authToken) => {
  try {
    const response = await axios.post(
      CREATE_COMMENT_URL(postId),
      { content },
      {
        headers: {
          authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Deletes a comment with a specified comment id provided it was created by the user
 * @function deleteComment
 * @param {string} commentId Id of the comment to delete
 * @param {string} authToken A user's auth token
 */
export const deleteComment = async (postId, commentId, authToken) => {
  try {
    await axios.delete(DELETE_COMMENT_URL(postId, commentId), {
      headers: {
        authorization: authToken,
      },
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Votes on a comment
 * @function voteComment
 * @param {string} commentId Id of the comment to vote on
 * @param {string} authToken A user's auth token
 */
export const voteComment = async (postId, commentId, authToken) => {
  try {
    await axios.post(
      VOTE_COMMENT_URL(postId, commentId),
      { reactionType: 'like' },
      { headers: { authorization: authToken } }
    );
  } catch (err) {
    throw new Error(err);
  }
};

export const removeCommentReaction = async (postId, commentId, authToken) => {
  try {
    await axios.delete(VOTE_COMMENT_URL(postId, commentId), {
      headers: { authorization: authToken },
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Creates a reply to a specific comment
 * @function createCommentReply
 * @param {string} message The message to be replied with to a comment
 * @param {string} parentCommentId The id of the comment to be replied to
 * @param {string} authToken A user's auth token
 * @returns {object} The created comment reply
 */
export const createCommentReply = async (
  content,
  postId,
  parentCommentId,
  authToken
) => {
  try {
    const response = await axios.post(
      CREATE_COMMENT_REPLY_URL(postId, parentCommentId),
      { content },
      {
        headers: {
          authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Deletes a comment reply with a specified comment reply id provided it was created by the user
 * @function deleteCommentReply
 * @param {string} commentReplyId Id of the comment reply to vote on
 * @param {string} authToken A user's auth token
 */
export const deleteCommentReply = async (postId, commentReplyId, authToken) => {
  try {
    await axios.delete(DELETE_COMMENT_REPLY_URL(postId, commentReplyId), {
      headers: {
        authorization: authToken,
      },
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Votes on a comment reply
 * @function voteCommentReply
 * @param {string} commentReplyId Id of the comment reply to vote on
 * @param {string} authToken A user's auth token
 */
export const voteCommentReply = async (postId, commentReplyId, authToken) => {
  try {
    await axios.post(
      VOTE_COMMENT_REPLY_URL(postId, commentReplyId),
      { reactionType: 'like' },
      { headers: { authorization: authToken } }
    );
  } catch (err) {
    throw new Error(err);
  }
};

export const removeCommentReplyReaction = async (postId, commentReplyId, authToken) => {
  try {
    await axios.delete(VOTE_COMMENT_REPLY_URL(postId, commentReplyId), {
      headers: { authorization: authToken },
    });
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Gets 3 new replies from a parent comment
 * @function getCommentReplies
 * @param {string} parentCommentId The id of a parent comment to get replies from
 * @param {number} offset A number to offset the results
 * @returns {array} Array of replies
 */
export const getCommentReplies = async (
  postId,
  parentCommentId,
  offset = 0,
  limit = 3
) => {
  try {
    const response = await axios.get(
      GET_COMMENT_REPLIES_URL(postId, parentCommentId, offset, limit)
    );
    // Return { replies, totalCount }
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Retrieves comments from a post with the given offset
 * @function getComments
 * @param {string} postId The id of a post to retrieve comments from
 * @param {number} offset The amount of comments to skip
 * @param {number} exclude The amount of comments to exlude (newest to oldest)
 * @returns {object} Object of comment details
 */
export const getComments = async (postId, offset, exclude = 0) => {
  try {
    const response = await axios.get(GET_COMMENTS_URL(postId, offset, exclude));
    // Server returns { comments, totalCount }
    return response.data;
  } catch (err) {
    throw new Error(err);
  }
};
